import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { Collaboration, ArtCategory } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { collaborationsService } from '../services/collaborationsService';

interface EditCollaborationModalProps {
  isOpen: boolean;
  collaboration: Collaboration;
  onClose: () => void;
  onSaved?: (updated: Collaboration) => void;
}

const EditCollaborationModal: React.FC<EditCollaborationModalProps> = ({
  isOpen,
  collaboration,
  onClose,
  onSaved,
}) => {
  const { select, t } = useLanguage();
  
  const [title, setTitle] = useState(collaboration.title || '');
  const [description, setDescription] = useState(collaboration.description || '');
  const [category, setCategory] = useState<ArtCategory>(collaboration.category);
  const [location, setLocation] = useState(collaboration.location || '');
  const [budget, setBudget] = useState<string>(collaboration.budget?.toString() || '');
  const [deadline, setDeadline] = useState(collaboration.deadline || '');
  const [requirements, setRequirements] = useState((collaboration.requirements || []).join('\n'));
  const [status, setStatus] = useState<'open' | 'in_progress' | 'completed' | 'cancelled'>(collaboration.status);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setTitle(collaboration.title || '');
    setDescription(collaboration.description || '');
    setCategory(collaboration.category);
    setLocation(collaboration.location || '');
    setBudget(collaboration.budget?.toString() || '');
    setDeadline(collaboration.deadline || '');
    setRequirements((collaboration.requirements || []).join('\n'));
    setStatus(collaboration.status);
    setError('');
  }, [isOpen, collaboration]);

  const categoryLabels = useMemo<Record<ArtCategory, string>>(
    () => ({
      music: t('categories.music', select({ es: 'Música', en: 'Music' })),
      writing: t('categories.writing', select({ es: 'Escritura', en: 'Writing' })),
      tattoo: t('categories.tattoo', select({ es: 'Tatuajes', en: 'Tattoos' })),
      digital_design: t('categories.digital_design', select({ es: 'Diseño Digital', en: 'Digital Design' })),
      fashion_design: t('categories.fashion_design', select({ es: 'Diseño de Moda', en: 'Fashion Design' })),
      photography: t('categories.photography', select({ es: 'Fotografía', en: 'Photography' })),
      sculpture: t('categories.sculpture', select({ es: 'Escultura', en: 'Sculpture' })),
      painting: t('categories.painting', select({ es: 'Pintura', en: 'Painting' })),
      dance: t('categories.dance', select({ es: 'Danza', en: 'Dance' })),
      filmmaking: t('categories.filmmaking', select({ es: 'Cine', en: 'Filmmaking' })),
      makeup: t('categories.makeup', select({ es: 'Maquillaje', en: 'Makeup' })),
    }),
    [select, t]
  );

  const statusLabels = {
    open: select({ es: 'Abierta', en: 'Open' }),
    in_progress: select({ es: 'En progreso', en: 'In progress' }),
    completed: select({ es: 'Completada', en: 'Completed' }),
    cancelled: select({ es: 'Cancelada', en: 'Cancelled' }),
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      const updates = {
        title: title.trim(),
        description: description.trim(),
        category,
        location: location.trim() || undefined,
        budget: budget ? parseFloat(budget) : undefined,
        deadline: deadline || undefined,
        requirements: requirements.split('\n').map(r => r.trim()).filter(r => r.length > 0),
        status,
      };

      const updated = await collaborationsService.updateCollaboration(collaboration.id, updates);
      
      if (onSaved) {
        // Merge the update with existing collaboration data to ensure we match the Collaboration type
        onSaved({ ...collaboration, ...updated } as Collaboration);
      }
      onClose();
    } catch (err) {
      console.error('Error updating collaboration:', err);
      setError(err instanceof Error ? err.message : select({ es: 'No se pudo actualizar la colaboración.', en: 'Failed to update the collaboration.' }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[10000]">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {select({ es: 'Editar colaboración', en: 'Edit collaboration' })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
              <AlertTriangle size={20} />
              <p>{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Título', en: 'Title' })}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {select({ es: 'Categoría', en: 'Category' })}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ArtCategory)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {select({ es: 'Estado', en: 'Status' })}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as 'open' | 'in_progress' | 'completed' | 'cancelled')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {Object.entries(statusLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Descripción', en: 'Description' })}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {select({ es: 'Ubicación', en: 'Location' })}
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder={select({ es: 'Ej: Madrid, Remoto...', en: 'Ex: Madrid, Remote...' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {select({ es: 'Presupuesto ($)', en: 'Budget ($)' })}
              </label>
              <input
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {select({ es: 'Fecha límite', en: 'Deadline' })}
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {select({ es: 'Requisitos (uno por línea)', en: 'Requirements (one per line)' })}
            </label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              rows={4}
              placeholder={select({ 
                es: '- Experiencia previa\n- Portafolio disponible\n- Disponibilidad inmediata', 
                en: '- Previous experience\n- Portfolio available\n- Immediate availability' 
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={saving}
          >
            {select({ es: 'Cancelar', en: 'Cancel' })}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save size={20} />
            )}
            {select({ es: 'Guardar cambios', en: 'Save changes' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCollaborationModal;
